import React, { useState } from "react";
import { authFetch } from "../../HttpClient";
import {
  Calendar,
  CheckCircle,
  RotateCcw,
  ArrowRight,
  Loader2,
  Check,
  Copy,
  AlertTriangle,
  CalendarPlus,
  Mail,
} from "lucide-react";

const TRANSITION_CONFIG = {
  "contact→rdv_futur": {
    Icon: Calendar,
    title: "Programmer un RDV",
    subtitle: "Planifier un rendez-vous avec ce prospect",
    needsRdvForm: true,
    dateMustBeFuture: true,
    confirmLabel: "Confirmer le RDV",
    iconBg: "bg-[#9B1B5D]",
    confirmBg: "bg-[#9B1B5D] hover:bg-[#7A1548]",
    isWarning: false,
  },
  "contact→rdv_passe": {
    Icon: CheckCircle,
    title: "RDV déjà effectué",
    subtitle: "Enregistrer un rendez-vous passé avec ce prospect",
    needsRdvForm: true,
    dateMustBeFuture: false,
    confirmLabel: "Enregistrer le RDV effectué",
    iconBg: "bg-emerald-600",
    confirmBg: "bg-emerald-600 hover:bg-emerald-700",
    isWarning: false,
  },
  "rdv_futur→contact": {
    Icon: RotateCcw,
    title: "Retour en prise de contact",
    subtitle: "Ce prospect reviendra dans la file des contacts",
    warningMsg: "Les informations de rendez-vous (date, heure, lieu) seront effacées.",
    needsRdvForm: false,
    confirmLabel: "Confirmer le retour",
    iconBg: "bg-amber-500",
    confirmBg: "bg-amber-500 hover:bg-amber-600",
    isWarning: true,
  },
  "rdv_futur→rdv_passe": {
    Icon: CheckCircle,
    title: "Marquer le RDV comme effectué",
    subtitle: "Confirmer que le rendez-vous s'est bien déroulé",
    needsRdvForm: false,
    confirmLabel: "Confirmer le RDV effectué",
    iconBg: "bg-emerald-600",
    confirmBg: "bg-emerald-600 hover:bg-emerald-700",
    isWarning: false,
  },
  "rdv_passe→contact": {
    Icon: RotateCcw,
    title: "Retour en prise de contact",
    subtitle: "Ce prospect reviendra dans la file des contacts",
    warningMsg: "Les informations de rendez-vous (date, heure, lieu) seront effacées.",
    needsRdvForm: false,
    confirmLabel: "Confirmer le retour",
    iconBg: "bg-amber-500",
    confirmBg: "bg-amber-500 hover:bg-amber-600",
    isWarning: true,
  },
  "rdv_passe→rdv_futur": {
    Icon: CalendarPlus,
    title: "Reprogrammer un RDV",
    subtitle: "Planifier un nouveau rendez-vous avec ce prospect",
    needsRdvForm: true,
    dateMustBeFuture: true,
    confirmLabel: "Reprogrammer le RDV",
    iconBg: "bg-[#9B1B5D]",
    confirmBg: "bg-[#9B1B5D] hover:bg-[#7A1548]",
    isWarning: false,
  },
};

function DragTransitionOverlay({ transition, users, onConfirm, onCancel }) {
  const { lead, from, to } = transition;
  const configKey = `${from}→${to}`;
  const config = TRANSITION_CONFIG[configKey];

  const [dateRDV, setDateRDV] = useState(
    from === "rdv_passe" && to === "rdv_futur" ? "" : ""
  );
  const [heureRDV, setHeureRDV] = useState(
    (from === "rdv_futur" || from === "rdv_passe") && to !== "contact"
      ? lead.heureRDV || ""
      : ""
  );
  const [lieuRDV, setLieuRDV] = useState(
    (from === "rdv_futur" || from === "rdv_passe") && to !== "contact"
      ? lead.lieuRDV || ""
      : ""
  );
  const [lienRDV, setLienRDV] = useState(
    (from === "rdv_futur" || from === "rdv_passe") && to !== "contact"
      ? lead.lienRDV || ""
      : ""
  );
  const [sendEmail, setSendEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedLien, setCopiedLien] = useState(false);
  // Collaborateur pour ce rdv — pré-rempli depuis lead.user s'il existe,
  // mais toujours modifiable et obligatoire dès qu'un formulaire de RDV est
  // affiché (sinon la confirmation envoyée au prospect n'identifie personne,
  // cf. incident du 01/09/2026 : mail "avec Monsieur" sans nom).
  const [userId, setUserId] = useState(lead.user?.id ? String(lead.user.id) : "");

  const generateJitsiLink = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 7);
    return `https://meet.jit.si/alfa-rdv-${timestamp}-${random}`;
  };

  if (!config) return null;

  const {
    Icon, title, subtitle, warningMsg,
    needsRdvForm, dateMustBeFuture,
    confirmLabel, iconBg, confirmBg, isWarning,
  } = config;

  const todayStr = new Date().toISOString().split("T")[0];
  const isFormValid =
    !needsRdvForm ||
    (dateRDV && heureRDV && lieuRDV && (lieuRDV !== "RDV en ligne" || lienRDV) && userId);

  const handleConfirm = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    const extraData = needsRdvForm ? { dateRDV, heureRDV, lieuRDV, lienRDV, userId } : {};
    onConfirm(extraData);

    if (from === "contact" && to === "rdv_futur" && sendEmail && lead.mail) {
      try {
        const dateObj = new Date(dateRDV);
        const localeDate = dateObj.toLocaleDateString("fr-FR", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        const formattedDate = localeDate.charAt(0).toUpperCase() + localeDate.slice(1);
        const formattedTime = heureRDV ? heureRDV.replace(":", "h") : "";
        const selectedUser = (users || []).find((u) => String(u.id) === String(userId));

        await authFetch("https://backend-myalfa.vercel.app/api/send-rdv-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            signer: lead.mail,
            nom: lead.nom,
            prenom: lead.prenom,
            dateRDV: formattedDate,
            lieuRDV,
            lienRDV,
            heureRDV: formattedTime,
            userName: selectedUser?.name || "",
            userLastName: selectedUser?.lastName || "",
            civilite: selectedUser?.civilite || "Monsieur",
            userMail: selectedUser?.email || "",
            idLead: String(lead.id),
          }),
        });
      } catch (err) {
        console.error("Erreur envoi email RDV :", err);
      }
    }
  };

  const toLabel =
    to === "contact" ? "Prise de contact" :
    to === "rdv_futur" ? "RDV à venir" : "RDV terminé";

  const toBadgeClass =
    to === "contact" ? "bg-blue-100 text-blue-700" :
    to === "rdv_futur" ? "bg-[#9B1B5D]/10 text-[#9B1B5D]" :
    "bg-emerald-100 text-emerald-700";

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-150">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
          <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">{title}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">

          {/* Lead info card */}
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
            <div className="w-8 h-8 rounded-full bg-[#9B1B5D]/10 flex items-center justify-center shrink-0">
              <span className="text-[#9B1B5D] font-bold text-xs uppercase">
                {(lead.prenom || "")[0]}{(lead.nom || "")[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {lead.civilite} {lead.prenom} {lead.nom}
              </p>
              <p className="text-xs text-slate-400 truncate">{lead.societeNom}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
            <span className={`text-xs font-medium px-2 py-1 rounded-md shrink-0 ${toBadgeClass}`}>
              {toLabel}
            </span>
          </div>

          {/* Warning */}
          {isWarning && warningMsg && (
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 rounded-xl border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">{warningMsg}</p>
            </div>
          )}

          {/* Current RDV info for rdv_futur → rdv_passe */}
          {from === "rdv_futur" && to === "rdv_passe" && lead.dateRDV && (
            <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
              <Calendar className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-800">
                RDV prévu le{" "}
                <strong>
                  {new Date(lead.dateRDV).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </strong>
                {lead.heureRDV ? ` à ${lead.heureRDV}` : ""}
              </p>
            </div>
          )}

          {/* RDV form */}
          {needsRdvForm && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <Calendar className="w-4 h-4 text-[#9B1B5D]" />
                <span className="text-sm font-semibold text-slate-700">
                  {dateMustBeFuture
                    ? "Informations du rendez-vous"
                    : "Informations du rendez-vous passé"}
                </span>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Date</label>
                    <input
                      type="date"
                      value={dateRDV}
                      onChange={(e) => setDateRDV(e.target.value)}
                      min={dateMustBeFuture ? todayStr : undefined}
                      max={!dateMustBeFuture ? todayStr : undefined}
                      className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Heure</label>
                    <input
                      type="time"
                      value={heureRDV}
                      onChange={(e) => setHeureRDV(e.target.value)}
                      className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Site</label>
                  <select
                    value={lieuRDV}
                    onChange={(e) => { const v = e.target.value; setLieuRDV(v); setLienRDV(v === "RDV en ligne" ? generateJitsiLink() : ""); }}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                  >
                    <option value="">— Site —</option>
                    <option value="27 rue Vallon Hoarau, LE TAMPON (97430)">
                      27 rue Vallon Hoarau, LE TAMPON (97430)
                    </option>
                    <option value="19 chemin du Château d'Eau, Espace Turquoise n° 23, SAINT-PIERRE (97410)">
                      19 ch. Château d'Eau, Espace Turquoise, ST-PIERRE (97410)
                    </option>
                    <option value="RDV en ligne">RDV en ligne</option>
                    <option value="Rendez-vous téléphonique">Rendez-vous téléphonique</option>
                  </select>
                </div>
                {lieuRDV === "RDV en ligne" && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Lien Jitsi Meet</label>
                    <div className="flex items-center gap-2 h-10 w-full rounded-lg border border-slate-200 px-3 bg-slate-50">
                      <span className="flex-1 text-xs text-slate-600 truncate">{lienRDV}</span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(lienRDV);
                          setCopiedLien(true);
                          setTimeout(() => setCopiedLien(false), 2000);
                        }}
                        className="shrink-0 flex items-center gap-1 text-xs font-medium text-[#9B1B5D] hover:text-[#7A1548] transition-colors"
                      >
                        {copiedLien ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            Copié !
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            Copier
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Personne chez Alfa
                  </label>
                  <select
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                  >
                    <option value="">— Collaborateur —</option>
                    {(users || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} {u.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {from === "contact" && to === "rdv_futur" && (
                  <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#9B1B5D]"
                    />
                    <span className="flex items-center gap-1.5 text-xs text-slate-600">
                      <Mail className="w-3.5 h-3.5 text-[#9B1B5D]" />
                      Envoyer un email de confirmation au prospect
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white">
          <button
            onClick={onCancel}
            className="px-6 h-10 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || !isFormValid}
            className={`flex items-center gap-2 px-6 h-10 ${confirmBg} text-white text-sm font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                En cours...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DragTransitionOverlay;
